import React, { useEffect, useState } from "react";
import axios from "axios";
import baseURL from "../../../config";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Modal,
  Box,
  IconButton,
} from "@mui/material";
import { Description, PictureAsPdf } from "@mui/icons-material";

const UserModal = ({ onClose, crewingAgent, updatedbankDetails }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDocumentModal, setOpenDocumentModal] = useState(false); // New state for document modal

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!crewingAgent) return;
      try {
        setLoading(true);
        const response = await axios.get(`${baseURL}/api/getDocumentsForAgent/${crewingAgent._id}`);
        setDocuments(response.data.documentTypes || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching documents:", error);
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [crewingAgent]);

  // Format documents for the modal
  const formatDocumentsForModal = () => {
    if (!documents || Object.keys(documents).length === 0) return [];
    
    return Object.keys(documents).map(key => ({
      fileName: documents[key].filename,
      filePath: documents[key].path,
      documentType: key
    }));
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle textAlign="center">Crewing Agent Information</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Section 1: Crewing Agent Details */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ padding: 2 }}>
              <Typography variant="h6" gutterBottom>
                Crewing Agent Details
              </Typography>
              {crewingAgent ? (
                <>
                  <Typography><strong>Crewing Agent:</strong> {crewingAgent.agentName || "N/A"}</Typography>
                  <Typography>
                    <strong>Contact Person:</strong> 
                    {crewingAgent.contactPerson
                      ? `${crewingAgent.contactPerson.title} ${crewingAgent.contactPerson.name}`
                      : "N/A"}
                  </Typography>
                  <Typography><strong>Contact Number:</strong> {crewingAgent.contactNumber || "N/A"}</Typography>
                  <Typography><strong>Email:</strong> {crewingAgent.email || "N/A"}</Typography>
                  <Typography><strong>Address:</strong> {crewingAgent.address || "N/A"}</Typography>
                </>
              ) : (
                <Typography>No agent details available.</Typography>
              )}
            </Paper>
          </Grid>

          {/* Section 2: Bank Details */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ padding: 2 }}>
              <Typography variant="h6" gutterBottom>
                🏦 Bank Details
              </Typography>
              {updatedbankDetails && updatedbankDetails.crewingAgentName ? (
                <>
                  <Typography><strong>Account Number:</strong> {updatedbankDetails.accountNumber}</Typography>
                  <Typography><strong>Bank Name:</strong> {updatedbankDetails.bankName}</Typography>
                  <Typography><strong>Branch Name:</strong> {updatedbankDetails.branchName}</Typography>
                  <Typography><strong>Bank Address:</strong> {updatedbankDetails.bankAdress}</Typography>
                  <Typography><strong>IFSC Code:</strong> {updatedbankDetails.ifscCode}</Typography>
                  <Typography><strong>Approved:</strong> {updatedbankDetails.approved ? "✅ Yes" : "❌ No"}</Typography>
                </>
              ) : (
                <Typography>No bank details available.</Typography>
              )}
            </Paper>
          </Grid>

          {/* Section 3: Uploaded Documents */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ padding: 2 }}>
              <Typography variant="h6" gutterBottom>
                📄 Contracts
              </Typography>

              {/* Documents Button - now opens modal instead of collapse */}
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<Description />} 
                onClick={() => setOpenDocumentModal(true)} 
                sx={{ marginBottom: 2 }}
                disabled={loading}
              >
                View Documents
              </Button>

              {loading && <CircularProgress size={24} />}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Close Button */}
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="error">
          Close
        </Button>
      </DialogActions>

      {/* Document Modal */}
      <Modal open={openDocumentModal} onClose={() => setOpenDocumentModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "60%", md: "50%" },
            maxHeight: "80vh",
            bgcolor: "white",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ textAlign: "center", mb: 2, fontWeight: "bold" }}
          >
            Contracts Documents
          </Typography>
          
          {Object.keys(documents).length > 0 ? (
            Object.keys(documents).map((key) => {
              let filePath = documents[key].path.trim();

              // Remove incorrect localhost prefix if S3 URL is detected
              if (filePath.startsWith("http://localhost:8000/https")) {
                filePath = filePath.replace("http://localhost:8000/", "");
              }

              // Finalize URL
              let downloadURL = filePath.startsWith("http") 
                ? filePath 
                : `${baseURL}/${filePath.replace(/\\/g, "/")}`;

              return (
                <Paper
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    mb: 2,
                    boxShadow: 2,
                    borderRadius: 2,
                    transition: "0.3s",
                    "&:hover": { boxShadow: 4, bgcolor: "#f5f5f5" },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <PictureAsPdf sx={{ color: "red", mr: 1 }} />
                    <Typography variant="body1">
                      {key}: {documents[key].filename || "Unknown Document"}
                    </Typography>
                  </Box>

                  {/* Check if filePath exists and is valid */}
                  {downloadURL ? (
                    <Button
                      variant="contained"
                      href={downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download
                    </Button>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No File Available
                    </Typography>
                  )}
                </Paper>
              );
            })
          ) : (
            <Typography align="center" color="textSecondary">
              No documents available
            </Typography>
          )}

          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="contained" color="error" onClick={() => setOpenDocumentModal(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Dialog>
  );
};

export default UserModal;